import { createVideoQna, partialUpdateVideoQna } from '@goldcast/api/backend';

import DialogService from './dialogs/DialogService';
import loggerService from './logger.service';

// @ts-ignore - Dynamic import for Vue component
import ConfirmationPopup from '@/components/core/ConfirmationPopup.vue';
import store from '@/store';
import { Broadcast } from '@/store/modules/event/types';

type VideoQNARequestStatus = 'OPEN' | 'ACCEPTED' | 'REJECTED' | 'COMPLETED' | 'CANCELLED';

export type VideoQNARequest = {
  broadcast: string;
  created_at: string;
  id: number;
  status: VideoQNARequestStatus;
  updated_at: string;
  user: string;
};

function getAllVideoQNARequestsForCurrentUser(broadcast: Broadcast) {
  const currentUserId = store.state.user.currentUser?.id;

  return broadcast?.video_qna_requests?.filter(request => request.user === currentUserId) || [];
}

export function getVideoQNARequestForCurrentUserByStatus(broadcast: Broadcast, status: VideoQNARequestStatus) {
  return getAllVideoQNARequestsForCurrentUser(broadcast)?.find(request => request.status === status);
}

export function currentUserHasNoVideoQNARequests(broadcast: Broadcast) {
  return getAllVideoQNARequestsForCurrentUser(broadcast).length === 0;
}

export function getVideoQNARequestsForBroadcastByStatus(broadcast: Broadcast, status: VideoQNARequestStatus) {
  return broadcast?.video_qna_requests?.filter(request => request.status === status) || [];
}

export function postNewVideoQNARequest(broadcastId: string) {
  store.commit('event/setIsVideoQNARequestLoading', true);
  createVideoQna({ body: { broadcast: broadcastId } }).catch(error => {
    loggerService.captureException(error as Error);
  });
}

export function updateVideoQNARequest(videoQNAId: any, status: VideoQNARequestStatus) {
  store.commit('event/setIsVideoQNARequestLoading', true);
  return partialUpdateVideoQna({
    id: videoQNAId,
    body: { status }
  }).catch(error => {
    loggerService.captureException(error as Error);
    throw error;
  });
}

export function getVisibleVideoQNARequests(requests: VideoQNARequest[]): VideoQNARequest[] {
  const acceptedRequests = requests.filter(request => request.status === 'ACCEPTED') || [];
  const openRequests = requests.filter(request => request.status === 'OPEN') || [];

  return [...acceptedRequests, ...openRequests];
}

export function showRaiseHandConsentDialog(onConfirm: () => void) {
  const i18n = store.state.event.translator?.i18n ?? { global: { t: (k: string) => k } };

  const dialogRef = DialogService.show(
    ConfirmationPopup,
    {
      title: i18n.global.t('@@translate@@ Recording consent'),
      message: i18n.global.t(
        '@@translate@@ This event is being recorded. By joining the stage, you are consenting to your audio and video being captured.'
      ),
      cancelText: i18n.global.t('@@translate@@ Cancel'),
      confirmText: i18n.global.t('@@translate@@ Okay'),
      type: 'warning'
    },
    {
      cancelOnClickOutside: true,
      blockBackgroundActions: true,
      closeOnCTAClick: false,
      on: {
        onConfirm: () => {
          onConfirm();
          DialogService.close(dialogRef.index);
        },
        onCancel: () => {
          DialogService.close(dialogRef.index);
        }
      }
    }
  );

  return dialogRef;
}
